import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import * as moment from 'moment';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { AlertService } from 'src/app/_services/alert.service';
import { CalendarService } from 'src/app/_services/calendar.service';
import { RequestService } from 'src/app/_services/request.service';
import { BaseComponent } from '../../../base/base.component';

@Component({
  selector: 'app-HolidayRequests',
  templateUrl: './HolidayRequests.component.html',
  styleUrls: [
    './HolidayRequests.component.css',
    '../RequestModal/RequestPermission.component.css',
    '../../review-modal/review-modal.component.css'
  ]
})
export class HolidayRequestsComponent extends BaseComponent implements OnInit {
  readonly requestsPerStep = 2;
  currentStep = 0;
  holidayRequestForm: FormGroup;
  private readonly alertService: AlertService = inject(AlertService);
  private readonly calendarService: CalendarService = inject(CalendarService);
  public rs: RequestService = inject(RequestService);
  @Output() saved = new EventEmitter<{ success: boolean; date: Date }>();

  // input arrivato dal componente Calendar
  readonly modalData: any = inject(NZ_MODAL_DATA);

  request?: any;
  user: any;
  date: string;
  edit: boolean;
  userEventDates: any[] = [];
  defaultPickerDate: Date | null = null;
  canCreatePastRequest = false;
  private readonly partTimeNonWorkingDateCache = new Map<string, boolean>();
  private readonly loadedPartTimeWeeks = new Set<string>();
  private readonly loadingPartTimeWeeks = new Set<string>();

  dateOptions: { label: string; value: string }[] = [];
  public formBuilder: FormBuilder = inject(FormBuilder);
  endDefaultPickerValue: Date = new Date();

  constructor(private readonly modalRef: NzModalRef) {
    super();
  }

  ngOnInit() {
    this.request = this.modalData.request;
    this.user = this.modalData.user;
    this.date = this.modalData.date;
    this.edit = this.modalData.edit;
    this.userEventDates = this.modalData.userEventDates ?? [];
    this.canCreatePastRequest = !!this.modalData.canCreatePastRequest;

    this.seedPartTimeWeekFromCalendarPayload();
    this._initForm();
    this.addRequest();

    // Imposta defaultPickerDate / startDate da modalData.date (se presente)
    if (this.date) {
      this.defaultPickerDate = moment(this.date).toDate();
    }

    // 2) Patch form se in edit
    if (this.request && this.edit) {
      this.patchForm(this.request);
      this.dateOptions = this.getDateArray(
        this.request.start_date,
        this.request.end_date
      );
    }

    // aggancia endDefaultPickerValue a startDate
    const dataArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;
    const firstGroup = dataArray.at(0) as FormGroup;
    const startCtrl = firstGroup.get('startDate');

    // iniziale (dopo patch/setValue)
    this.endDefaultPickerValue = this.toDate(startCtrl?.value) ?? new Date();

    const endCtrl = firstGroup.get('endDate');

    startCtrl?.valueChanges.subscribe(v => {
      this.endDefaultPickerValue = this.toDate(v) ?? new Date();
      this.resetEndDateControl(endCtrl);
    });
    this.loadUserEventDates(this.getReferenceDateForUserEventDates());
  }

  private isDateDisabledForRequest(current: Date): boolean {
    if (this.isPartTimeNonWorkingDate(current)) {
      return true;
    }

    if (this.canCreatePastRequest) {
      return this.utilityService.disabledDate(current);
    }

    return this.disablePastAndWeekendDates(current, true);
  }

  private toDate(value: any): Date | null {
    if (!value) return null;

    if (value instanceof Date) return value;

    if (moment.isMoment(value)) return value.toDate();

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Inizializza la cache FE con la settimana già caricata nel calendario,
   * così i datepicker bloccano subito i giorni non lavorativi part-time
   * senza attendere ulteriori chiamate.
   */
  private seedPartTimeWeekFromCalendarPayload(): void {
    const calendarDays = this.modalData?.user?.days ?? [];

    calendarDays.forEach((day: any) => {
      if (!day?.date || !day?.part_time_schedule?.is_part_time) {
        return;
      }

      this.partTimeNonWorkingDateCache.set(
        day.date,
        !day.part_time_schedule.is_working_day
      );
      this.loadedPartTimeWeeks.add(
        moment(day.date).startOf('isoWeek').format('YYYY-MM-DD')
      );
    });
  }

  /**
   * Carica on demand la distribuzione effettiva della settimana richiesta
   * quando l'utente naviga il datepicker fuori dalla settimana già visibile.
   */
  private ensurePartTimeWeekLoaded(date: Date | null): void {
    if (!date || !this.user?.id) {
      return;
    }

    const weekStart = moment(date).startOf('isoWeek').format('YYYY-MM-DD');

    if (
      this.loadedPartTimeWeeks.has(weekStart) ||
      this.loadingPartTimeWeeks.has(weekStart)
    ) {
      return;
    }

    this.loadingPartTimeWeeks.add(weekStart);

    this.rs.getPartTimeWeek(this.user.id, weekStart).subscribe({
      next: (response: any) => {
        const result = response?.result;
        const days = result?.days ?? [];
        const isPartTime = !!result?.contract?.is_part_time;

        if (isPartTime) {
          days.forEach((day: any) => {
            const dateKey = moment(weekStart)
              .add((day.weekday ?? 1) - 1, 'days')
              .format('YYYY-MM-DD');

            this.partTimeNonWorkingDateCache.set(dateKey, !day.is_working_day);
          });
        }

        this.loadedPartTimeWeeks.add(weekStart);
        this.loadingPartTimeWeeks.delete(weekStart);
      },
      error: () => {
        this.loadingPartTimeWeeks.delete(weekStart);
      }
    });
  }

  /**
   * Dice se la data cade su un giorno non lavorativo della distribuzione
   * part-time effettiva dell'utente.
   */
  private isPartTimeNonWorkingDate(date: Date | null): boolean {
    if (!date) {
      return false;
    }

    const dateKey = moment(date).format('YYYY-MM-DD');
    this.ensurePartTimeWeekLoaded(date);

    return this.partTimeNonWorkingDateCache.get(dateKey) === true;
  }

  // Usa gli eventi già caricati dal BE e tiene solo quelli compresi nel range selezionato nella riga corrente.
  getExistingEventsForRequest(index: number): any[] {
    const control = (
      this.holidayRequestForm.get('dataHolidayArray') as FormArray
    )?.at(index);
    const startDate = this.toDate(control?.get('startDate')?.value);
    const endDate = this.toDate(control?.get('endDate')?.value) ?? startDate;

    if (!(startDate && endDate)) {
      return [];
    }

    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');

    return this.userEventDates.filter((event: any) => {
      const eventDate = moment(event.date, 'YYYY-MM-DD');
      return eventDate.isBetween(start, end, 'day', '[]');
    });
  }

  // una richiesta ferie non può proseguire se nel periodo selezionato esistono già permessi.
  hasBlockingConflict(index: number): boolean {
    return (
      !this.edit &&
      this.getExistingEventsForRequest(index).some(
        (event: any) => event.eventType === 'Permesso'
      )
    );
  }

  // Disabilita il bottone Invia se conflitto eventi esiste
  get hasBlockingConflicts(): boolean {
    return this.newRequestControls.some((_, index) =>
      this.hasBlockingConflict(index)
    );
  }

  // Se c'è conflitto mostra il messaggio bloccante; altrimenti mostra il riepilogo degli eventi già presenti.
  getExistingEventsAlertText(index: number): string {
    if (this.hasBlockingConflict(index)) {
      return 'Hai già un permesso in questo periodo. Eliminalo per procedere o modifica le date della richiesta.';
    }

    const formatted = this.getExistingEventsForRequest(index).map(event =>
      this.formatExistingEvent(event)
    );
    return [...new Set(formatted)].join(' • ');
  }

  // formatta testo degli eventi
  formatExistingEvent(event: any): string {
    if (event?.startTime && event?.endTime) {
      const start = event.startTime.slice(0, 5);
      const end = event.endTime.slice(0, 5);
      return start === end
        ? `${event.eventType} ${start}`
        : `${event.eventType} ${start} - ${end}`;
    }
    return event?.eventType ?? '';
  }

  // Richiama il BE quando cambia la data di inizio e aggiorna modale
  private loadUserEventDates(date: Date | null): void {
    if (!date || !this.user?.id) {
      return;
    }
    this.calendarService
      .getUserEventDates(moment(date).format('YYYY-MM-DD'), this.user.id)
      .subscribe({
        next: (res: any) => {
          this.userEventDates = res?.events ?? [];
        }
      });
  }

  // Usa la data di inizio più vecchia tra le righe per non perdere il contesto
  // quando si modifica una richiesta successiva.
  private getReferenceDateForUserEventDates(): Date | null {
    const dataArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;

    const dates = dataArray.controls
      .map(ctrl => this.toDate(ctrl.get('startDate')?.value))
      .filter((d): d is Date => !!d)
      .sort((a, b) => a.getTime() - b.getTime());

    return dates[0] ?? null;
  }

  /**
   * Pulisce endDate mantenendo lo stato "pristine/untouched".
   */
  private resetEndDateControl(endCtrl: AbstractControl | null): void {
    endCtrl?.setValue(null);
    endCtrl?.markAsPristine();
    endCtrl?.markAsUntouched();
  }

  /**
   * Ricalcola la validità delle date dopo modifiche al FormArray.
   */
  private refreshDateRangeValidity(dataArray: FormArray): void {
    dataArray.controls.forEach(control => {
      control.get('startDate')?.updateValueAndValidity({ emitEvent: false });
      control.get('endDate')?.updateValueAndValidity({ emitEvent: false });
    });
    dataArray.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Restituisce gli intervalli selezionati nelle altre righe.
   */
  private getOtherRanges(
    index: number
  ): Array<{ start: moment.Moment; end: moment.Moment }> {
    const dataArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;

    return dataArray.controls
      .map((ctrl, i) => ({ ctrl, i }))
      .filter(({ i }) => i !== index)
      .map(({ ctrl }) => {
        const start = this.toDate(ctrl.get('startDate')?.value);
        const end = this.toDate(ctrl.get('endDate')?.value);
        if (!(start && end)) return null;

        const startMoment = moment(start).startOf('day');
        const endMoment = moment(end).startOf('day');
        if (endMoment.isBefore(startMoment, 'day')) return null;

        return { start: startMoment, end: endMoment };
      })
      .filter(
        (range): range is { start: moment.Moment; end: moment.Moment } =>
          !!range
      );
  }

  /**
   * Verifica se la data è interna a uno degli intervalli passati.
   */
  private isDateInsideRanges(
    date: moment.Moment,
    ranges: Array<{ start: moment.Moment; end: moment.Moment }>
  ): boolean {
    return ranges.some(range =>
      date.isBetween(range.start, range.end, 'day', '[]')
    );
  }

  /**
   * Verifica se l'intervallo candidato si sovrappone a intervalli esistenti.
   */
  private intervalOverlapsRanges(
    start: moment.Moment,
    end: moment.Moment,
    ranges: Array<{ start: moment.Moment; end: moment.Moment }>
  ): boolean {
    return ranges.some(
      range =>
        start.isSameOrBefore(range.end, 'day') &&
        range.start.isSameOrBefore(end, 'day')
    );
  }

  /**
   * Disabilita la data di inizio se weekend/passato o già compresa in altri range.
   */
  disabledStartDateForIndex =
    (index: number) =>
    (current: Date): boolean => {
      if (this.isDateDisabledForRequest(current)) return true;

      const currentDate = moment(current).startOf('day');
      const ranges = this.getOtherRanges(index);
      return this.isDateInsideRanges(currentDate, ranges);
    };

  /**
   * Disabilita la data di fine se non coerente con startDate o sovrapposta ad altri range.
   */
  disabledEndDateForIndex =
    (index: number) =>
    (current: Date): boolean => {
      if (this.isDateDisabledForRequest(current)) return true;

      const dataArray = this.holidayRequestForm.get(
        'dataHolidayArray'
      ) as FormArray;
      const group = dataArray.at(index) as FormGroup;
      const startValue = this.toDate(group.get('startDate')?.value);

      const currentDate = moment(current).startOf('day');
      const ranges = this.getOtherRanges(index);

      if (!startValue) {
        return this.isDateInsideRanges(currentDate, ranges);
      }

      const startDate = moment(startValue).startOf('day');
      if (currentDate.isBefore(startDate, 'day')) return true;

      return this.intervalOverlapsRanges(startDate, currentDate, ranges);
    };

  /**
   * Inizializzo il form
   */
  _initForm() {
    this.holidayRequestForm = this.formBuilder.group({
      dataHolidayArray: this.formBuilder.array([]),
      excludedDays: new FormControl([]),
      event_type_id: 7
    });
  }

  /**
   * Restituisce i controlli del FormArray ferie.
   */
  get newRequestControls() {
    return (this.holidayRequestForm.get('dataHolidayArray') as FormArray)
      .controls;
  }

  get excludedDaysControl(): FormControl {
    return this.holidayRequestForm.get('excludedDays') as FormControl;
  }

  get totalSteps(): number {
    const steps = Math.ceil(
      this.newRequestControls.length / this.requestsPerStep
    );
    return Math.max(steps, 1);
  }

  get visibleRequests(): Array<{ control: AbstractControl; index: number }> {
    const start = this.currentStep * this.requestsPerStep;
    return this.newRequestControls
      .slice(start, start + this.requestsPerStep)
      .map((control, offset) => ({ control, index: start + offset }));
  }

  get stepDots(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i);
  }

  trackByVisibleRequest(
    _: number,
    item: { control: AbstractControl; index: number }
  ): AbstractControl {
    return item.control;
  }

  trackByNumber(_: number, value: number): number {
    return value;
  }

  /**
   * Aggiunge una nuova richiesta ferie al FormArray.
   */
  addRequest(): void {
    const defaultDate = this.date ? moment(this.date).toDate() : null;

    (this.holidayRequestForm.get('dataHolidayArray') as FormArray).push(
      this.formBuilder.group({
        startDate: [defaultDate, Validators.required],
        endDate: ['', Validators.required],
        notes: [null, Validators.required]
      })
    );
    this.currentStep = this.totalSteps - 1;
  }

  /**
   * Patch del form in caso di modifica di una richiesta di ferie
   * @param data dati della richiesta di ferie da modificare
   */
  patchForm(data: any): void {
    // prima resetto l'array
    const arrayControl = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;
    arrayControl.clear();

    // aggiungo un nuovo gruppo per la richiesta
    arrayControl.push(
      this.formBuilder.group({
        startDate: [data.start_date, Validators.required],
        endDate: [data.end_date, Validators.required],
        notes: [data.note ?? null, Validators.required]
      })
    );

    // patcho le altre proprietà
    this.holidayRequestForm.patchValue({
      event_type_id: 7 // fisso come nel form
    });
  }

  /**
   * Cancella una riga del FormArray ferie e riallinea la paginazione.
   */
  deleteRequest(target: number | AbstractControl) {
    const requestsArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;
    const index =
      typeof target === 'number'
        ? target
        : requestsArray.controls.indexOf(target);

    if (index >= 0 && requestsArray.length > index) {
      requestsArray.removeAt(index);
      this.refreshDateRangeValidity(requestsArray);
      this.currentStep = Math.min(this.currentStep, this.totalSteps - 1);
      this.loadUserEventDates(this.getReferenceDateForUserEventDates());
    }
  }

  /**
   * Naviga allo step precedente.
   */
  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  /**
   * Naviga allo step successivo.
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
    }
  }

  /**
   * Gestisce le modifiche a startDate pulendo endDate quando necessario.
   */
  onStartDateChange(index: number): void {
    const dataArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;
    const group = dataArray.at(index) as FormGroup;
    const startDate = this.toDate(group.get('startDate')?.value);
    const endCtrl = group.get('endDate');
    const endDate = this.toDate(endCtrl?.value);

    if (!startDate) {
      this.resetEndDateControl(endCtrl);
      return;
    }

    this.endDefaultPickerValue = startDate;

    if (endDate && moment(endDate).isBefore(moment(startDate), 'day')) {
      this.resetEndDateControl(endCtrl);
    }

    this.loadUserEventDates(this.getReferenceDateForUserEventDates());
  }

  saveHolidayRequest() {
    // Converto la data poco prima di inviarla al BE
    const formData = this.holidayRequestForm.value;
    const payload = {
      ...formData,
      user_id: this.user?.id,
      date_array: formData.dataHolidayArray.map((item: any) => ({
        startDate: moment(item.startDate).format('YYYY/MM/DD'),
        endDate: moment(item.endDate).format('YYYY/MM/DD'),
        notes: item.notes ?? null,
        start_time: '00:00',
        end_time: '18:00'
      }))
    };
    // cancello dati che non voglio passare al BE
    delete payload.dataHolidayArray;
    const firstDate = new Date(payload.date_array[0].startDate);

    this.rs.storeRequest(payload).subscribe({
      next: () => {
        // Richiamo l'alert direttamente nell'AlertService
        const text = 'di ferie';
        this.alertService.showSuccessRequestAlert(text);
        // chiudo la modale
        this.saved.emit({ success: true, date: firstDate });
        this.modalRef.close();
      },
      error: () => {
        this.alertService.showErrorRequestAlert();
      }
    });
  }

  updateRequest() {
    const formData = this.holidayRequestForm.value;
    const obj = {
      event_id: this.request.id,
      event_type_id: 7,
      notes: formData.dataHolidayArray[0]?.notes ?? null,
      excluded_days: formData.excludedDays,
      start_time: '09:00',
      end_time: '18:00'
    };
    const firstDate = new Date(formData.dataHolidayArray[0].startDate);
    this.rs.updateRequest(obj).subscribe({
      next: () => {
        // Richiamo l'alert direttamente nell'AlertService
        const text = 'di ferie';
        this.alertService.showSuccessRequestAlert(text);
        this.saved.emit({ success: true, date: firstDate });
        this.modalRef.close();
      },
      error: () => {
        this.alertService.showErrorRequestAlert();
      }
    });
  }

  /**
   * Limita la nota della singola richiesta a 250 caratteri.
   */
  limitTextLength(index: number): void {
    const dataArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;
    const control = dataArray.at(index)?.get('notes');
    if (control) {
      const text = control.value || '';
      if (text.length > 250) {
        control.setValue(text.substring(0, 250), { emitEvent: false });
      }
    }
  }

  /**
   * Restituisce la lunghezza corrente della nota della riga indicata.
   */
  getRequestNotesLength(index: number): number {
    const dataArray = this.holidayRequestForm.get(
      'dataHolidayArray'
    ) as FormArray;
    return (dataArray.at(index)?.get('notes')?.value || '').length;
  }

  /**
   * Restituisce tutte le date tra un range (start - end),
   * escludendo sabati (6) e domeniche (0),
   * con label contenente anche il giorno della settimana.
   *
   * @param start data inizio (formato stringa ISO)
   * @param end data fine (formato stringa ISO)
   * @returns array di oggetti con { label, value }
   */
  getDateArray(start: string, end: string): { label: string; value: string }[] {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options: { label: string; value: string }[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      const dayOfWeek = current.getDay(); // 0 = domenica, 6 = sabato

      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // yyyy-MM-dd (valore da salvare)
        const yyyy = current.getFullYear();
        const mm = String(current.getMonth() + 1).padStart(2, '0');
        const dd = String(current.getDate()).padStart(2, '0');

        const value = `${yyyy}-${mm}-${dd}`;

        // Giorno della settimana con prima lettera maiuscola
        let weekday = current.toLocaleDateString('it-IT', { weekday: 'long' });
        weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);

        // Label: "Lunedì 01-09-2025"
        const label = `${weekday} ${dd}-${mm}-${yyyy}`;

        options.push({ label, value });
      }

      current.setDate(current.getDate() + 1);
    }

    return options;
  }

  /**
   * chiudi modale
   */
  closeModal() {
    this.modalRef.close();
  }
}
